// src/views/examples/AdminAddEmployee.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import {
  API_GETZIPCODE,
  API_ADDEMPLOYEE,
  API_GETPOSITIONS,
  API_GETBRANCHS,
} from "../../config/api";
import { api } from "../../config/middleware";
import { useSessionStore } from "../../stores/sessionStore";
import Swal from "sweetalert2";

const AdminAddEmployee = () => {
  const navigate = useNavigate();
  const orgId = useSessionStore((s) => s.profile?.orgId) ?? "";

  // ---------- รูปพนักงาน ----------
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const logoInputRef = useRef(null);
  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const objectUrl = URL.createObjectURL(file);
    setLogoPreview(objectUrl);
  };
  useEffect(
    () => () => logoPreview && URL.revokeObjectURL(logoPreview),
    [logoPreview],
  );

  // ---------- Positions ----------
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingPositions(true);
        const res = await api.get(API_GETPOSITIONS);
        const raw =
          res?.data?.data?.rows ??
          res?.data?.data ??
          res?.data?.rows ??
          res?.data ??
          [];
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((x) => ({
          psId: x.psId ?? x.PSID ?? x.psid,
          psName: x.psName ?? x.PSNAME ?? x.psname,
          psStatus: x.psStatus ?? x.PSSTATUS ?? x.psstatus,
        }));
        if (alive) setPositions(normalized.filter((p) => p.psId));
      } catch {
        if (alive) setPositions([]);
      } finally {
        if (alive) setLoadingPositions(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---------- Branches ----------
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    (async () => {
      try {
        setLoadingBranches(true);
        const res = await api.get(API_GETBRANCHS, {
          params: { ORGID: orgId, PAGE: 1, SIZE: 500, Q: "" },
        });
        const raw =
          res?.data?.data?.rows ??
          res?.data?.rows ??
          res?.data?.data ??
          res?.data ??
          [];
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((b) => ({
          brId: b.brId ?? b.BRID ?? b.brid,
          brCode: b.brCode ?? b.BRCODE ?? b.brcode,
          brNameTH: b.brNameTH ?? b.BRNAMETH ?? b.brnameth,
        }));
        if (alive) setBranches(normalized.filter((b) => b.brId));
      } catch {
        if (alive) setBranches([]);
      } finally {
        if (alive) setLoadingBranches(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId]);

  // ---------- ฟอร์ม ----------
  const [form, setForm] = useState({
    EMPUSERNAME: "",
    EMPPASSWORD: "",
    EMPFIRSTNAME: "",
    EMPLASTNAME: "",
    EMPDOB: "",
    EMPIDCARD: "",
    EMPADDR: "",
    EMPADDR2: "",
    EMPSUBDISTRICT: "",
    EMPDISTRICT: "",
    EMPPROVINCE: "",
    EMPZIPCODE: "",
    EMPMOBILE: "",
    EMPEMAIL: "",
    EMPSSOHOSPITAL: "",
    PSID: "",
    BRID: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  // ✅ ซ่อนสาขาเมื่อเลือกตำแหน่งเหล่านี้
  const shouldHideBranch = useMemo(() => {
    const psid = String(form.PSID || "").trim();
    if (!psid) return false;

    const pos = positions.find((p) => String(p.psId) === psid);
    const name = (pos?.psName || "").toLowerCase().trim();

    // ✅ ไม่ซ่อนกรณี branch manager / branch admin
    if (name.includes("branch manager")) return false;
    if (name.includes("branch admin")) return false;

    // ✅ ซ่อนเฉพาะ super admin / manager / admin (ที่ไม่ใช่ branch)
    const keywords = [
      "super admin",
      "superadmin",
      "manager",
      "admin",
      "ซุปเปอร์แอดมิน",
      "ผู้ดูแล",
      "แอดมิน",
      "ผู้จัดการ",
    ];

    return keywords.some((k) => name.includes(k));
  }, [form.PSID, positions]);

  // ✅ ถ้าซ่อนสาขา ให้ล้างค่า BRID กันส่งผิด
  useEffect(() => {
    if (shouldHideBranch && form.BRID) {
      setForm((s) => ({ ...s, BRID: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHideBranch]);

  // ---------- ZIP search ----------
  const [zipQuery, setZipQuery] = useState("");
  const [zipList, setZipList] = useState([]);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const zipBoxRef = useRef(null);

  useEffect(() => {
    if (zipQuery === "") {
      setZipList([]);
      setZipOpen(false);
      return;
    }
    const t = setTimeout(() => {
      setZipLoading(true);
      const current = zipQuery;
      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== zipQuery) return;
          const rows = res?.data?.data ?? [];
          setZipList(rows);
          setZipOpen(true);
        })
        .catch(() => {
          if (current !== zipQuery) return;
          setZipList([]);
          setZipOpen(false);
        })
        .finally(() => {
          if (current !== zipQuery) return;
          setZipLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [zipQuery]);

  const onZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, EMPZIPCODE: v }));
    setZipQuery(v);
  };

  const onPickZip = (item) => {
    setForm((s) => ({
      ...s,
      EMPZIPCODE: String(item.zip_code ?? ""),
      EMPSUBDISTRICT: item.subdistrict_name ?? "",
      EMPDISTRICT: item.district_name ?? "",
      EMPPROVINCE: item.province_name ?? "",
    }));
    setZipOpen(false);
    setZipList([]);
    setZipQuery("");
  };

  // ---------- Submit (multipart + file) ----------
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    try {
      if (!orgId) {
        await Swal.fire({
          icon: "warning",
          title: "ไม่พบองค์กร",
          text: "กรุณาเข้าสู่ระบบใหม่",
        });
        return;
      }
      if (!form.PSID) {
        await Swal.fire({
          icon: "warning",
          title: "กรุณาเลือกตำแหน่ง",
          text: "ต้องระบุ PSID",
        });
        return;
      }

      // ✅ ถ้าไม่ซ่อนสาขา ถึงจะบังคับเลือก
      if (!shouldHideBranch && !form.BRID) {
        await Swal.fire({
          icon: "warning",
          title: "กรุณาเลือกสาขา",
          text: "ต้องระบุ BRID",
        });
        return;
      }

      const fd = new FormData();
      fd.append("ORGID", orgId);
      fd.append("PSID", String(form.PSID));

      // ✅ ถ้าซ่อนสาขา ไม่ต้องส่ง BRID (หรือส่งเป็น "" ก็ได้ แต่ปลอดภัยคือไม่ส่ง)
      if (!shouldHideBranch) fd.append("BRID", String(form.BRID));

      fd.append("EMPUSERNAME", form.EMPUSERNAME);
      fd.append("EMPPASSWORD", form.EMPPASSWORD);
      fd.append("EMPFIRSTNAME", form.EMPFIRSTNAME);
      fd.append("EMPLASTNAME", form.EMPLASTNAME);
      fd.append("EMPDOB", form.EMPDOB || "");
      fd.append("EMPIDCARD", form.EMPIDCARD || "");
      fd.append("EMPADDR", form.EMPADDR || "");
      fd.append("EMPADDR2", form.EMPADDR2 || "");
      fd.append("EMPSUBDISTRICT", form.EMPSUBDISTRICT || "");
      fd.append("EMPDISTRICT", form.EMPDISTRICT || "");
      fd.append("EMPPROVINCE", form.EMPPROVINCE || "");
      fd.append("EMPZIPCODE", form.EMPZIPCODE || "");
      fd.append("EMPMOBILE", form.EMPMOBILE || "");
      fd.append("EMPEMAIL", form.EMPEMAIL || "");
      fd.append("EMPSSOHOSPITAL", form.EMPSSOHOSPITAL || "");
      if (logoFile) fd.append("EMPPICTURE", logoFile);

      const res = await api.post(API_ADDEMPLOYEE, fd);

      await Swal.fire({
        icon: "success",
        title: res?.data?.message,
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/employee");
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "บันทึกไม่สำเร็จ",
        text: err?.response?.data?.message || err?.message || "เกิดข้อผิดพลาด",
      });
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">เพิ่มข้อมูลพนักงาน</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <h6 className="heading-small text-muted mb-4">
                    ข้อมูลพื้นฐาน
                  </h6>

                  {/* อัปโหลดรูป */}
                  <Row className="justify-content-center">
                    <Col lg="6" md="8" sm="10" className="mx-auto">
                      <FormGroup className="text-center">
                        <label
                          className="form-control-label d-block"
                          htmlFor="emp-photo-input"
                        >
                          รูปพนักงาน
                        </label>
                        <div className="d-flex align-items-center justify-content-center w-100 mb-3">
                          <div
                            className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 100,
                              height: 100,
                              overflow: "hidden",
                              border: "1px solid #e9ecef",
                              cursor: "pointer",
                            }}
                            onClick={() => logoInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              (e.key === "Enter" || e.key === " ") &&
                              logoInputRef.current?.click()
                            }
                            aria-label="อัปโหลดรูปพนักงาน"
                          >
                            {logoPreview ? (
                              <img
                                alt="ตัวอย่างรูปพนักงาน"
                                src={logoPreview}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span className="text-muted small">เลือกรูป</span>
                            )}
                          </div>
                        </div>
                        <Input
                          innerRef={logoInputRef}
                          id="emp-photo-input"
                          type="file"
                          accept="image/*"
                          onChange={onLogoChange}
                          className="d-none"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="pl-lg-4">
                    {/* ชื่อ-นามสกุล */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">ชื่อ</label>
                          <Input
                            name="EMPFIRSTNAME"
                            value={form.EMPFIRSTNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">นามสกุล</label>
                          <Input
                            name="EMPLASTNAME"
                            value={form.EMPLASTNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Username/Password */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Username</label>
                          <Input
                            name="EMPUSERNAME"
                            value={form.EMPUSERNAME}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Password</label>
                          <Input
                            name="EMPPASSWORD"
                            value={form.EMPPASSWORD}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ตำแหน่ง + สาขา */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">ตำแหน่ง</label>
                          <Input
                            type="select"
                            className="form-control-alternative"
                            name="PSID"
                            value={form.PSID}
                            onChange={(e) =>
                              setForm((s) => ({ ...s, PSID: e.target.value }))
                            }
                          >
                            <option value="">
                              {loadingPositions
                                ? "กำลังโหลด…"
                                : "-- เลือกตำแหน่ง --"}
                            </option>
                            {Array.isArray(positions) &&
                              positions.map((p) => (
                                <option key={p.psId} value={p.psId}>
                                  {p.psName}
                                </option>
                              ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      {/* ✅ ซ่อนสาขาเมื่อเป็น Super Admin / Manager / Admin */}
                      {!shouldHideBranch && (
                        <Col lg="6">
                          <FormGroup>
                            <label className="form-control-label">สาขา</label>
                            <Input
                              type="select"
                              className="form-control-alternative"
                              name="BRID"
                              value={form.BRID}
                              onChange={(e) =>
                                setForm((s) => ({ ...s, BRID: e.target.value }))
                              }
                            >
                              <option value="">
                                {loadingBranches
                                  ? "กำลังโหลด…"
                                  : "-- เลือกสาขา --"}
                              </option>
                              {Array.isArray(branches) &&
                                branches.map((b) => (
                                  <option key={b.brId} value={b.brId}>
                                    {b.brCode
                                      ? `${b.brCode} - ${b.brNameTH ?? ""}`
                                      : (b.brNameTH ?? "")}
                                  </option>
                                ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      )}
                    </Row>

                    {/* วันเกิด / รพ. */}
                    <Row>
                      <Col lg="6" md="6">
                        <FormGroup className="mb-0">
                          <label className="form-control-label">
                            วันเดือนปีเกิด
                          </label>
                          <Input
                            type="date"
                            name="EMPDOB"
                            value={form.EMPDOB}
                            onChange={onChange}
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            โรงพยาบาลประกันสังคม
                          </label>
                          <Input
                            name="EMPSSOHOSPITAL"
                            value={form.EMPSSOHOSPITAL}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <h6 className="heading-small text-muted mb-4">
                      ช่องทางการติดต่อ
                    </h6>

                    {/* ที่อยู่ */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">ที่อยู่</label>
                          <Input
                            name="EMPADDR"
                            value={form.EMPADDR}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">
                            ที่อยู่เพิ่มเติม
                          </label>
                          <Input
                            name="EMPADDR2"
                            value={form.EMPADDR2}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zip + ตำบล/อำเภอ/จังหวัด */}
                    <Row>
                      <Col lg="3">
                        <FormGroup
                          style={{ position: "relative" }}
                          innerRef={zipBoxRef}
                        >
                          <label className="form-control-label">
                            รหัสไปรษณีย์
                          </label>
                          <Input
                            name="EMPZIPCODE"
                            value={form.EMPZIPCODE}
                            onChange={onZipChange}
                            onFocus={() => zipList.length && setZipOpen(true)}
                            autoComplete="off"
                            type="text"
                            className="form-control-alternative"
                          />
                          {zipOpen && (
                            <div
                              className="dropdown-menu show w-100"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1050,
                                maxHeight: 240,
                                overflowY: "auto",
                              }}
                            >
                              {zipLoading && (
                                <div className="dropdown-item text-muted small">
                                  กำลังค้นหา…
                                </div>
                              )}
                              {!zipLoading && zipList.length === 0 && (
                                <div className="dropdown-item text-muted small">
                                  ไม่พบข้อมูลรหัสนี้
                                </div>
                              )}
                              {!zipLoading &&
                                zipList.map((it, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="dropdown-item d-flex flex-column align-items-start"
                                    onClick={() => onPickZip(it)}
                                  >
                                    <div>
                                      <strong>{it.zip_code}</strong>
                                    </div>
                                    <div className="small text-muted">
                                      {it.subdistrict_name} • {it.district_name}{" "}
                                      • {it.province_name}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">
                            ตำบล/แขวง
                          </label>
                          <Input
                            disabled
                            name="EMPSUBDISTRICT"
                            value={form.EMPSUBDISTRICT}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">
                            อำเภอ/เขต
                          </label>
                          <Input
                            disabled
                            name="EMPDISTRICT"
                            value={form.EMPDISTRICT}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label className="form-control-label">จังหวัด</label>
                          <Input
                            disabled
                            name="EMPPROVINCE"
                            value={form.EMPPROVINCE}
                            onChange={onChange}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* เบอร์/อีเมล */}
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">เบอร์โทร</label>
                          <Input
                            name="EMPMOBILE"
                            value={form.EMPMOBILE}
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                "",
                              );
                              setForm((s) => ({ ...s, EMPMOBILE: onlyNums }));
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                            type="text"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label">Email</label>
                          <Input
                            name="EMPEMAIL"
                            value={form.EMPEMAIL}
                            onChange={onChange}
                            type="email"
                            className="form-control-alternative"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ModalFooter>
        <Button color="secondary" onClick={() => navigate("/employee")}>
          ยกเลิก
        </Button>
        <Button color="primary" type="submit" onClick={handleSubmit}>
          บันทึก
        </Button>
      </ModalFooter>
    </>
  );
};

export default AdminAddEmployee;
